import React, { ReactNode } from 'react';

import { Button, Icon } from '@tecma/ds';
import classNames from 'classnames';
import { Trans, useTranslation } from '@tecma/i18n';

import './highlighted-content.scss';

interface HighlightedContentButtonProps {
  label?: string;
  onClick: () => void;
  iconName?: string;
}

interface HighlightedContentRequiredProps {}

// Optional Props
interface HighlightedContentOptionalProps {
  iconName?: string;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  color?: 'primary' | 'inverse' | 'transparent';
  firstButton?: HighlightedContentButtonProps;
  secondButton?: HighlightedContentButtonProps;
  horizontal?: boolean;
  contactUsButton?: {
    label: string;
    onClick: () => void;
  };
}

// Combined required and optional props to build the full prop interface
export interface HighlightedContentProps extends HighlightedContentRequiredProps, HighlightedContentOptionalProps {}

const HighlightedContent: React.FC<HighlightedContentProps> = ({
  iconName,
  title,
  subtitle,
  color = 'primary',
  firstButton,
  secondButton,
  horizontal,
  contactUsButton,
}) => {
  const { t } = useTranslation();
  const classList = classNames('highlighted-content', {
    [`${color}`]: color,
    horizontal,
  });

  return (
    <div className={classList}>
      {iconName && <Icon iconName={iconName} size='large' />}
      <div className='text-content'>
        {title && <span className='title'>{title}</span>}
        {subtitle && <span className='subtitle'>{subtitle}</span>}
      </div>
      {(firstButton || secondButton) && (
        <div className='cta-content'>
          {firstButton && (
            <Button fluid onClick={firstButton.onClick} iconName={firstButton.iconName} color={color === 'inverse' ? 'inverse' : 'primary'}>
              {firstButton.label}
            </Button>
          )}
          {secondButton && (
            <Button fluid outlined onClick={secondButton.onClick} iconName={secondButton.iconName}>
              {secondButton.label}
            </Button>
          )}
        </div>
      )}
      {contactUsButton && (
        <section className='contact-us-section'>
          <Trans
            i18nKey={contactUsButton.label}
            components={{
              span: <span />,
              button: <Button link color={color === 'inverse' ? 'inverse' : 'primary'} onClick={contactUsButton.onClick} />,
            }}
          />
        </section>
      )}
    </div>
  );
};

export default React.memo(HighlightedContent);
